/**
 * JoinAddPopover — the [+ add join] chooser. Lists FK / loose-rel candidates
 * (click → commit an INNER JOIN with the auto-derived ON) AND every other
 * reachable table (click → pick the ON columns, since there's no FK to derive
 * one from). So a JOIN can be added even when no foreign key links the tables.
 *
 * Mounts the populated popover only when an `editContext` is supplied AND the
 * parent `FromSection` resolved a join anchor (snapshot loaded). The RO /
 * not-connected state renders the disabled scaffold trigger via
 * `CommandPickerPopover`. The TanStack Query subscriptions live in the
 * `*Enabled` sub-component so VQE-RO unit tests don't need a `QueryClientProvider`.
 *
 * PRD: Ralph/VisualQueryEditorEdit/PRD.md § Slice: Edit — add/remove JOIN +
 * edit ON predicate.
 */

import { useState } from 'react';
import { Plus } from 'lucide-react';

import { Popover, PopoverContent, PopoverTrigger } from '../../../../../components/ui/popover';
import { Button } from '../../../../../components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../../../components/ui/tooltip';
import type { QualifiedName } from '../../../../sql-grammar/model';
import type { VqeEditContext } from '../../edit/editContext';
import { useJoinSuggestions, useTableSuggestions } from '../../edit/useSchemaSuggestions';
import { vqeCopy } from '../../ux/vqeCopy';
import { CommandPickerPopover } from '../common/CommandPickerPopover';
import { JoinAddContent } from './JoinAddContent';

export interface JoinAddPopoverProps {
  readonly editContext?: VqeEditContext;
  /** The FROM-side anchor table (typically the leading FROM item). */
  readonly leftTable?: QualifiedName;
  /** The textual alias of `leftTable` to use in the generated ON predicate. */
  readonly leftAlias?: string;
}

const TEST_ID = 'vqe-from-add-join';

export function JoinAddPopover({ editContext, leftTable, leftAlias }: JoinAddPopoverProps) {
  if (!editContext || !leftTable || !leftAlias) {
    return <CommandPickerPopover triggerLabel={vqeCopy.actions.addJoin} testId={TEST_ID} />;
  }
  return (
    <JoinAddPopoverEnabled
      editContext={editContext}
      leftTable={leftTable}
      leftAlias={leftAlias}
    />
  );
}

interface EnabledProps {
  readonly editContext: VqeEditContext;
  readonly leftTable: QualifiedName;
  readonly leftAlias: string;
}

function JoinAddPopoverEnabled({ editContext, leftTable, leftAlias }: EnabledProps) {
  const [open, setOpen] = useState(false);
  const { candidates, isLoading: candidatesLoading } = useJoinSuggestions({
    service: editContext.service,
    scope: editContext.scope,
    leftTable,
    cacheKey: `${editContext.documentId}::${editContext.statementId}::${leftTable.foldedName}`,
  });
  const { tables, isLoading: tablesLoading } = useTableSuggestions({
    service: editContext.service,
    scope: editContext.scope,
    cacheKey: `${editContext.documentId}::${editContext.statementId}::join`,
  });

  const snapshot = editContext.service.getSchemaSnapshot();
  const loading = candidatesLoading || tablesLoading;
  const enabled = !!snapshot && (candidates.length > 0 || tables.length > 0 || loading);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Tooltip>
        {/* Span-wrapped so the disabled-reason tooltip still opens on hover —
            a disabled Button is pointer-events:none, like CommandPickerPopover's. */}
        <TooltipTrigger asChild>
          <span className="inline-flex">
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 gap-1 px-2 text-xs text-muted-foreground"
                disabled={!enabled}
                aria-disabled={!enabled}
                data-testid={TEST_ID}
              >
                <Plus className="h-3.5 w-3.5" />
                {vqeCopy.actions.addJoin}
              </Button>
            </PopoverTrigger>
          </span>
        </TooltipTrigger>
        {!enabled ? <TooltipContent side="top">{vqeCopy.disabled.addJoinNoTables}</TooltipContent> : null}
      </Tooltip>
      <PopoverContent
        align="start"
        className="w-72 p-0 text-xs"
        data-testid={`${TEST_ID}-content`}
        // Like CommandPickerPopover: a late schema-refresh re-render shifts focus
        // and would fire Radix's focus-outside dismiss mid-selection. Outside
        // pointer-down still dismisses.
        onFocusOutside={(e) => e.preventDefault()}
      >
        {snapshot ? (
          <JoinAddContent
            testId={TEST_ID}
            editContext={editContext}
            leftTable={leftTable}
            leftAlias={leftAlias}
            candidates={candidates}
            tables={tables}
            snapshot={snapshot}
            loading={loading}
            onClose={() => setOpen(false)}
          />
        ) : null}
      </PopoverContent>
    </Popover>
  );
}
